import React, { useState } from "react";
import { api } from "../api";
import { toast } from "sonner";

// Compact newsletter subscription form. Drop it anywhere in the site.
// Props:
//   - source: string tag saved with each subscriber ("contact" | "footer" | ...)
//   - variant: "block" (default, standalone section) | "inline" (single row)
const Newsletter = ({ source = "contact", variant = "block" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api.subscribeNewsletter({ email, name, source });
      setDone(true);
      setEmail("");
      setName("");
      toast(
        r.already_subscribed
          ? "You're already on the list — thank you."
          : "You're on the list. Thank you.",
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not subscribe. Try again shortly.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (variant === "inline") {
    return (
      <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-3" data-testid="newsletter-inline">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[15px] min-w-[240px]"
          data-testid="newsletter-email"
        />
        <button
          type="submit"
          disabled={busy || done}
          className="btn-sober disabled:opacity-50"
          data-testid="newsletter-submit"
        >
          {done ? "Subscribed ✓" : busy ? "Sending…" : "Subscribe"}
        </button>
      </form>
    );
  }

  return (
    <section className="border-t border-[color:var(--line)] pt-10" data-testid="newsletter-block">
      <p className="font-serif italic text-[color:var(--ink-mute)] mb-2">Newsletter</p>
      <p className="font-serif text-2xl md:text-3xl leading-[1.3] max-w-xl mb-6">
        A few emails a year — new work, upcoming books, exhibition dates.
      </p>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-4 max-w-xl">
        <label className="flex-1 min-w-[180px]">
          <span className="font-serif italic text-[color:var(--ink-mute)] text-sm">Name (optional)</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[15px]"
            data-testid="newsletter-name"
          />
        </label>
        <label className="flex-[2] min-w-[220px]">
          <span className="font-serif italic text-[color:var(--ink-mute)] text-sm">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="mt-1 w-full bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[15px]"
            data-testid="newsletter-email-block"
          />
        </label>
        <button
          type="submit"
          disabled={busy || done}
          className="btn-sober disabled:opacity-50"
          data-testid="newsletter-submit-block"
        >
          {done ? "Subscribed ✓" : busy ? "…" : "Subscribe"}
        </button>
      </form>
      <p className="font-serif text-[12px] text-[color:var(--ink-mute)] italic mt-3">
        No spam. Unsubscribe any time.
      </p>
    </section>
  );
};

export default Newsletter;
